import type { RegulationId } from '@dcs/content';

/**
 * Vietnamese strings of the home page (`/`). Neutral, short, no greeting (design D-9); every
 * number in them comes from data (`virtual:theory-counts`, ProgressRepo, the latest attempt).
 */
export const HOME_MESSAGES = {
  'home.classLabel': 'Hạng',
  'home.continueTitle': 'Việc đang làm',
  'home.start': 'Bắt đầu ôn lý thuyết',
  'home.theoryTitle': 'Lý thuyết',
  'home.theoryLink': 'Ôn lý thuyết',
  'home.criticalLink': 'Câu điểm liệt',
  'home.examTitle': 'Thi thử',
  'home.examLink': 'Thi thử',
  'home.examGuest': 'Đăng nhập để lưu kết quả',
  'home.examNone': 'Chưa có lượt thi đã lưu',
  'home.examLoading': 'Đang tải lần thi gần nhất',
  'home.examError': 'Chưa tải được lần thi gần nhất.',
  'home.examOffline': 'Đang offline, chưa tải được lần thi gần nhất.',
  'home.passed': 'Đạt',
  'home.failed': 'Trượt',
  'home.simExam': 'Thi sa hình 3D',
  'home.practiceTitle': 'Sa hình 3D',
  'home.drive': 'Lái tự do',
  'home.exercises': 'Luyện từng bài',
} as const;

export type HomeMessageCode = keyof typeof HOME_MESSAGES;

export function ht(code: HomeMessageCode): string {
  return HOME_MESSAGES[code];
}

/** Short name of a regulation set on the "Đề hiện hành" chip. */
export const REGULATION_SHORT: Readonly<Record<RegulationId, string>> = {
  'tt12-2025': 'TT12',
  'tt108-2026': 'TT108',
};

/** Full name, for the chip's tooltip. */
export const REGULATION_FULL: Readonly<Record<RegulationId, string>> = {
  'tt12-2025': 'Thông tư 12/2025/TT-BCA',
  'tt108-2026': 'Thông tư 108/2026/TT-BCA',
};

/** The "Tiếp tục" label of a chapter, in two parts: the button never breaks inside `at`. */
export interface ContinueChapterLabel {
  readonly lead: string;
  readonly at: string;
}

export const homeFns = {
  regulationChip: (id: RegulationId): string => `Đề hiện hành ${REGULATION_SHORT[id]}`,
  /** "Tiếp tục: Chương 2 ·" and "câu 4/25", which the button keeps on one line. */
  continueChapter: (chapter: number, position: number, total: number): ContinueChapterLabel => ({
    lead: `Tiếp tục: Chương ${String(chapter)} ·`,
    at: `câu ${String(position)}/${String(total)}`,
  }),
  reviewWrong: (count: number): string => `Ôn lại ${String(count)} câu sai`,
  retakeExam: (classLabel: string): string => `Làm lại đề hạng ${classLabel}`,
  takeExam: (classLabel: string): string => `Làm đề thi thử hạng ${classLabel}`,
  examOfClass: (classLabel: string): string => `Đề hạng ${classLabel}`,
  wrongLink: (count: number): string => `Câu sai (${String(count)})`,
  /** A start under 1% reads "<1%", not "0%" (0% is only for nothing right yet). */
  theoryStat: (correct: number, total: number, percent: number): string =>
    `${correct > 0 && percent < 1 ? '<1' : String(percent)}% · ${String(correct)}/${String(total)} câu đúng`,
  theoryProgressLabel: (classLabel: string): string => `Tiến độ lý thuyết hạng ${classLabel}`,
  theoryProgressValue: (correct: number, total: number): string =>
    `${String(correct)} trên ${String(total)} câu đúng`,
  latestAttempt: (passed: boolean, score: number, maxScore: number, classLabel: string): string =>
    `Lần gần nhất: ${passed ? HOME_MESSAGES['home.passed'] : HOME_MESSAGES['home.failed']} ${String(score)}/${String(maxScore)} · hạng ${classLabel}`,
  exercisesStat: (count: number): string => `${String(count)} bài · theo QCVN 15:2026`,
};
